import axios from "axios";
import { SERVER_URL } from "Constants";
import { catchErrorAsync } from "Util/CatchErrorAsync";
import alertToast from 'Util/ToastifyUtils';
import { OrganisationProviderSummary, ProviderTypeDto } from "../types/CreditProviderItemStoreType";
import { CreditProviderDto } from "../types/CreditProviderListStoreTypes";
import { CreditProviderProductDto, ProviderProductCreateDto } from "../types/CreditProviderProductStoreType";
import { OrganisationProductSummary } from "../types/OrganisationProviderProductStoreTypes";
import { PaginatedResponse } from "Types/PaginatedResponse";

const getCreditProviders = async (pageNo: number = 1, pageSize: number = 10) => {
	const requestOptions =  {
		params: {
			pageNo: pageNo,
			pageSize: pageSize
		}
	};
	const apiCall = axios
		.get<PaginatedResponse<CreditProviderDto>>(`${SERVER_URL}/api/provider/list`, requestOptions)
		.then(response =>  response.data);
		
	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		return {
			data: [],
			pageNo: 1,
			pageSize: 10,
			totalCount: 0,
			totalPages: 0
		} as PaginatedResponse<CreditProviderDto>;
	}

	return data;
};

const addCreditProvider = async (creditProviderDto: CreditProviderDto) => {
	const apiCall = axios.post<string>(`${SERVER_URL}/api/provider/add`, creditProviderDto).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		return undefined;
	}
	return data;

}

const updateCreditProvider = async (creditProviderId: string, creditProviderDto: Partial<CreditProviderDto>) => {
	const apiCall = axios.put<boolean>(`${SERVER_URL}/api/provider/${creditProviderId}/update`, creditProviderDto).then(response => {
		return response.data;
	});
	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		return false;
	}
	return data;
}

const getCreditProviderDetail = async (creditProviderId: string) => {
	const apiCall = axios.get<CreditProviderDto>(`${SERVER_URL}/api/provider/${creditProviderId}`).then(response => {
		return response.data;
	});
	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		console.error("Error getting credit provider summary");
		alertToast("Error getting credit provider summary", "error");
		return undefined;
	}
	return data;
};

const getProviderType = async () => {
	const apiCall = axios.get<ProviderTypeDto[]>(`${SERVER_URL}/api/provider/list/type`).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		console.error("Error getting provider types");
		return [];
	}
	return data;
}

const getOrganisationProviders = async(pageNo: number = 1, pageSize: number = 10, providerId: string) => {
	const requestParams: { pageNo: number, pageSize: number} = {
		pageNo: pageNo,
		pageSize: pageSize
	};

	const apiCall = axios.get<PaginatedResponse<OrganisationProviderSummary>>(`${SERVER_URL}/api/provider/${providerId}/organisation`, {
		params: requestParams
	}).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast(" Failed to retrieve the organisation list ", "error");
		return {
			data: [],
			pageNo: 1,
			pageSize: 10,
			totalCount: 0,
			totalPages: 0
		} as PaginatedResponse<OrganisationProviderSummary>;
	} 

	return data;
}

const updateOrganisationProviderLink = async (providerId: string, enableProduct: boolean, organisationId?: string) => {
	const apiCall = axios.post<void>(`${SERVER_URL}/api/provider/${providerId}/link/organisation`, {
		enableProduct: enableProduct,
		organisationId: organisationId,
	}).then(response => {
		return response.data;
	});

	const [error, _] = await catchErrorAsync(apiCall);
	if (error) {
		return true;
	}
	return true;
}

const getAvailableProviderProducts = async (providerId: string, pageNo: number = 1, pageSize: number = 10) => {
	const params: { pageNo: number, pageSize: number } = {
		pageNo: pageNo,
		pageSize: pageSize,
	}

	const apiCall = axios.get<PaginatedResponse<CreditProviderProductDto>>(`${SERVER_URL}/api/provider/${providerId}/product`, {
		params: params
	}).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Failed to retrieve the available products", "error");
		return {
			data: [],
			pageNo: 1,
			pageSize: 10,
			totalCount: 0,
			totalPages: 0
		}
	}

	return data;
}

const getProviderProduct = async (providerId: string, productId: string) => {
	const apiCall = axios.get<CreditProviderProductDto>(`${SERVER_URL}/api/provider/${providerId}/product/${productId}`).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Failed to retrieve the product", "error");
		return undefined;
	}

	return data;
}

const saveProviderProduct = async (providerId: string, product: CreditProviderProductDto): Promise<CreditProviderProductDto| undefined> => {
	const apiCall = axios.post<CreditProviderProductDto>(`${SERVER_URL}/api/provider/${providerId}/product`, product).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Failed to save the product", "error");
		return undefined;
	}

	if (product.id !== data.id) {
		alertToast(`New Product code with ${product.productCode} has been created`, "success");
	}else {
		alertToast("Product has been updated successfully", "success");
	}
	return data;
}

const getAvailableProductOrganisations = async (providerId: string, productId: string, pageNo: number = 1, pageSize: number = 10) => {
	const params: { pageNo: number, pageSize: number } = {
		pageNo: pageNo,
		pageSize: pageSize
	};

	const apiCall = axios.get<PaginatedResponse<OrganisationProductSummary>>(`${SERVER_URL}/api/provider/${providerId}/product/${productId}/organisation`, {
		params: params
	}).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Failed to retrieve the available organisations", "error");
		return {
			data: [],
			pageNo: 1,
			pageSize: 10,
			totalCount: 0,
			totalPages: 0
		} as PaginatedResponse<OrganisationProductSummary>;
	}

	return data;
}

const toggleProductForOrganisation = async (providerId: string, productId: string, enableProduct: boolean, organisationId?: string) => {
	const apiCall = axios.post<string>(`${SERVER_URL}/api/provider/${providerId}/product/${productId}/enable-disable`, {
		organisationId: organisationId,
		enableProduct: enableProduct,
	}).then(response => {
		return response.data;
	});

	const [error, data] = await catchErrorAsync(apiCall);
	const alertText = enableProduct ? "enable" : "disable";
	if (error || !data) {
		alertToast(`Failed to ${alertText} the product for all organisations`, "error");
		return false;
	}
	if (data) {
		alertToast(`Product has been successfully ${alertText} for all organisations`, "success");
	}
	return true;
}

const updateOrganisationProductDetail = async (data: OrganisationProductSummary) => {
	const apiCall = axios.post<void>(`${SERVER_URL}/api/provider/${data.providerId}/product/${data.providerProductId}/organisation`, data).then(response => {
		return response.data;
	});

	const [error, _] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Failed to update the product detail", "error");
		return false;
	}

	alertToast("Product detail has been updated", "success");
	return true;
}

const createProviderProducts = async (providerId: string, products: ProviderProductCreateDto[]) => {
	const apiCall = axios.post<void>(`${SERVER_URL}/api/provider/${providerId}/product/add`, products).then(response => {
		return response.data;
	});

	const [error, _] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Failed to create the products", "error");
		return false;
	}

	alertToast("Products have been created", "success");
	return true;
}


export const CreditProviderEndpoints = {
	getCreditProviders: getCreditProviders,
	addCreditProvider: addCreditProvider,
	updateCreditProvider: updateCreditProvider,
	getCreditProviderDetail: getCreditProviderDetail,
	getProviderType: getProviderType,
	getOrganisationProviders: getOrganisationProviders,
	updateOrganisationProviderLink: updateOrganisationProviderLink,
	getAvailableProviderProducts: getAvailableProviderProducts,
	getProviderProduct: getProviderProduct,
	saveProviderProduct: saveProviderProduct,
	getAvailableProductOrganisations: getAvailableProductOrganisations,
	toggleProductForOrganisation: toggleProductForOrganisation,
	updateOrganisationProductDetail: updateOrganisationProductDetail,
	createProviderProducts: createProviderProducts,
};